import * as React from 'react'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {MealsType} from '@vio/core/src/offer/types/AugmentedOffer'

import {TextAlignedIcon} from './styles'

import type {MealPlan, MealRule} from '@findhotel/sapi'

const getGuestCount = (rule: MealRule): number | undefined => {
  const count = rule.guests ?? (rule.adults ?? 0) + (rule.kids ?? 0)
  return count > 0 ? count : undefined
}

interface Props {
  /** SAPI meal plan entries; primary data source for meal display */
  mealPlan?: MealPlan[]
  /** Applies sold-out styling to visually indicate that the offer is no longer available. */
  isOfferSoldOut?: boolean
  /** Size of the meal icon and text */
  size?: 'sm' | 'md'
  /** Hides guest count when rule data is present */
  hideGuestCount?: boolean
  /**
   * Controls the visual style:
   * - 'offer' (default): success green, labelS/bodyM text — for offer listings
   * - 'summary': neutral palette, bodyS text — for booking/post-booking contexts
   */
  variant?: 'offer' | 'summary'
}

const MEAL_MESSAGES: Partial<
  Record<MealsType, (count: number) => JSX.Element>
> = {
  breakfast: (count: number) => (
    <FormattedMessageWrapper
      id="mealPlan.breakfast"
      defaultMessage="{count, plural, =0 {Breakfast included} one {Breakfast included for # guest} other {Breakfast included for # guests}}"
      values={{count}}
    />
  ),

  lunch: (count: number) => (
    <FormattedMessageWrapper
      id="mealPlan.lunch"
      defaultMessage="{count, plural, =0 {Lunch included} one {Lunch included for # guest} other {Lunch included for # guests}}"
      values={{count}}
    />
  ),

  dinner: (count: number) => (
    <FormattedMessageWrapper
      id="mealPlan.dinner"
      defaultMessage="{count, plural, =0 {Dinner included} one {Dinner included for # guest} other {Dinner included for # guests}}"
      values={{count}}
    />
  ),

  fullBoard: (count: number) => (
    <FormattedMessageWrapper
      id="mealPlan.fullBoard"
      defaultMessage="{count, plural, =0 {Breakfast, lunch and dinner} one {Breakfast, lunch and dinner included for # guest} other {Breakfast, lunch and dinner included for # guests}}"
      values={{count}}
    />
  ),

  halfBoard: (count: number) => (
    <FormattedMessageWrapper
      id="mealPlan.halfBoard"
      defaultMessage="{count, plural, =0 {Breakfast and dinner} one {Breakfast and dinner included for # guest} other {Breakfast and dinner included for # guests}}"
      values={{count}}
    />
  ),

  allInclusive: (count: number) => (
    <FormattedMessageWrapper
      id="mealPlan.allInclusive"
      defaultMessage="{count, plural, =0 {All inclusive} one {All inclusive for # guest} other {All inclusive for # guests}}"
      values={{count}}
    />
  )
}

const MealName = ({
  mealPlan,
  hideGuestCount
}: Pick<Props, 'mealPlan' | 'hideGuestCount'>) => {
  if (!mealPlan?.[0]) return null

  const firstMeal = mealPlan[0]

  const guestCount =
    !hideGuestCount && firstMeal.rule
      ? getGuestCount(firstMeal.rule)
      : undefined

  const mealMessage = MEAL_MESSAGES[firstMeal.meal as MealsType]
  if (!mealMessage) return null

  return mealMessage(guestCount ?? 0)
}

export const Meal = ({
  mealPlan,
  isOfferSoldOut,
  size = 'sm',
  hideGuestCount,
  variant = 'offer'
}: Props) => {
  if (!mealPlan?.length) {
    return null
  }

  const iconColorPath = isOfferSoldOut
    ? 'content.neutral.disabled'
    : variant === 'summary'
      ? 'content.neutral.secondary'
      : 'content.success.secondary'

  const textColorPath = isOfferSoldOut
    ? 'content.neutral.disabled'
    : variant === 'summary'
      ? 'content.neutral.primary'
      : 'content.success.secondary'

  const offerTextVariant = size === 'sm' ? 'labelS' : 'bodyM'

  const textVariant = variant === 'summary' ? 'bodyS' : offerTextVariant

  return (
    <Box display="flex" gap="s250" alignItems="baseline">
      <TextAlignedIcon
        name="Restaurant"
        size={size}
        colorPath={iconColorPath}
      />
      <Text variant={textVariant} colorPath={textColorPath} dataId="boardType">
        <MealName mealPlan={mealPlan} hideGuestCount={hideGuestCount} />
      </Text>
    </Box>
  )
}
